import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { MessageSquare } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface CoachFeedbackDialogProps {
  bookingId: string;
  existingFeedback?: string;
  onFeedbackSaved?: () => void;
}

export const CoachFeedbackDialog = ({ bookingId, existingFeedback, onFeedbackSaved }: CoachFeedbackDialogProps) => {
  const [open, setOpen] = useState(false);
  const [feedback, setFeedback] = useState(existingFeedback || "");
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!feedback.trim()) {
      toast.error("Please enter feedback before saving");
      return;
    }

    setSaving(true);
    try {
      const { error } = await supabase
        .from("bookings")
        .update({ coach_feedback: feedback })
        .eq("id", bookingId);

      if (error) throw error;

      toast.success("Feedback saved successfully");
      setOpen(false);
      onFeedbackSaved?.();
    } catch (error) {
      console.error("Error saving feedback:", error);
      toast.error("Failed to save feedback");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant={existingFeedback ? "outline" : "default"} size="sm">
          <MessageSquare className="w-4 h-4 mr-2" />
          {existingFeedback ? "Edit Feedback" : "Leave Feedback"}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Lesson Feedback</DialogTitle>
          <DialogDescription>
            Share your observations and recommendations with the student
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <Textarea
            placeholder="Enter your feedback here... (e.g., vocal technique improvements, areas of strength, practice suggestions)"
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            rows={8}
            className="resize-none"
          />
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? "Saving..." : "Save Feedback"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
